import { useEffect, useRef } from "react"

import type { AnimationEvent } from "@/types"

import { NarrativeEntry } from "./narrative-entry"
import type { PlayerLookupEntry } from "./player-name-links"

interface NarrativePanelProps {
  events: AnimationEvent[]
  currentEventIndex: number
  homeTeamId: string
  awayTeamId: string
  homeTeamName: string
  awayTeamName: string
  homeTeamHue?: number
  awayTeamHue?: number
  /** Called with the player ID when user hovers a player name in the narrative. */
  onPlayerHover?: (playerId: string) => void
  /** Called when the player name hover ends. */
  onPlayerHoverEnd?: () => void
  /** Called with the event index when the user clicks a narrative entry. */
  onSeek?: (index: number) => void
  /** Player name lookup for inline hover cross-links. */
  playerLookup?: PlayerLookupEntry[]
  /** Optional style overrides for the root container (e.g. mobile overlay mode). */
  style?: React.CSSProperties
}

type PanelItem =
  | {
      kind: "header"
      turnLabel: string
      teamId: string | null
      play: string | null
      key: string
    }
  | {
      kind: "play_change"
      play: string
      key: string
    }
  | {
      kind: "entry"
      event: AnimationEvent
      index: number
      key: string
    }

const PLAY_LABELS: Record<string, string> = {
  cage_drive: "Cage Drive",
  score_now: "Score Now",
  stall: "Stall",
  pressure: "Pressure",
  contain: "Contain",
  ball_recovery: "Ball Recovery",
}

/**
 * Play-by-play narrative panel for the match replay viewer.
 *
 * Desktop: 300px-wide sidebar to the right of the pitch.
 * Mobile: rendered inside a fixed overlay (controlled by parent).
 *
 * Features:
 * - Turn group headers with team color dot
 * - Colored action badge per entry
 * - Auto-scrolls the active entry into view during playback
 * - Inactive entries are dimmed
 * - Player name hover fires onPlayerHover/onPlayerHoverEnd for pitch cross-link
 */
export function NarrativePanel({
  events,
  currentEventIndex,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  homeTeamHue = 145,
  awayTeamHue = 275,
  onPlayerHover,
  onPlayerHoverEnd,
  onSeek,
  playerLookup,
  style,
}: NarrativePanelProps) {
  const activeEntryRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to the active entry when the index changes
  useEffect(() => {
    if (activeEntryRef.current) {
      activeEntryRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    }
  }, [currentEventIndex])

  // Build grouped items: turn headers + play change dividers + entries
  const items: PanelItem[] = []
  let lastTurnLabel: string | null = null
  let lastActingTeamId: string | null = null
  let lastPlay: string | null = null

  for (let i = 0; i < events.length; i++) {
    const event = events[i]
    const turnChanged =
      event.turnLabel !== lastTurnLabel ||
      event.actingTeamId !== lastActingTeamId

    // Insert a turn header when the turn or acting team changes
    if (turnChanged && (event.turnLabel || event.actingTeamId)) {
      items.push({
        kind: "header",
        turnLabel: event.turnLabel ?? "Play",
        teamId: event.actingTeamId,
        play: event.play,
        key: `header-${i}`,
      })
      lastTurnLabel = event.turnLabel
      lastActingTeamId = event.actingTeamId
      lastPlay = event.play
    } else if (event.play && event.play !== lastPlay) {
      // Mid-turn play change — insert a divider
      items.push({
        kind: "play_change",
        play: event.play,
        key: `play-change-${i}`,
      })
      lastPlay = event.play
    }

    items.push({
      kind: "entry",
      event,
      index: i,
      key: `entry-${i}`,
    })
  }

  return (
    <div
      data-testid="narrative-panel"
      style={{
        width: "300px",
        flexShrink: 0,
        background: "oklch(0.12 0.005 230)",
        borderLeft: "2px solid oklch(0.25 0.01 230)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...style,
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "2px solid oklch(0.25 0.01 230)",
          background: "oklch(0.14 0.005 230)",
          flexShrink: 0,
        }}
      >
        <span
          className="font-display"
          style={{
            color: "oklch(0.80 0.16 85)",
            fontSize: "14px",
            fontWeight: 700,
          }}
        >
          Play-by-Play
        </span>
      </div>

      {/* Scrollable entry list */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: "8px",
        }}
      >
        {items.map((item) => {
          if (item.kind === "header") {
            const isHome = item.teamId === homeTeamId
            const isAway = item.teamId === awayTeamId
            const teamLabel = isHome
              ? homeTeamName
              : isAway
                ? awayTeamName
                : null
            const teamColor = isHome
              ? `oklch(0.55 0.15 ${homeTeamHue})`
              : `oklch(0.55 0.15 ${awayTeamHue})`

            return (
              <div
                key={item.key}
                style={{
                  padding: "8px 14px 4px",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "oklch(0.12 0.005 230)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    borderBottom: "1px solid oklch(0.18 0.005 230)",
                    paddingBottom: "4px",
                  }}
                >
                  {teamLabel && (
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: teamColor,
                        flexShrink: 0,
                        display: "inline-block",
                      }}
                      aria-hidden="true"
                    />
                  )}
                  <span
                    className="font-display"
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "oklch(0.6 0.12 85)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {item.turnLabel}
                    {teamLabel ? ` — ${teamLabel}` : ""}
                  </span>
                  {item.play && PLAY_LABELS[item.play] && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: "9px",
                        fontWeight: 600,
                        color: "oklch(0.55 0.12 145)",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {PLAY_LABELS[item.play]}
                    </span>
                  )}
                </div>
              </div>
            )
          } else if (item.kind === "play_change") {
            return (
              <div
                key={item.key}
                style={{
                  padding: "4px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: "oklch(0.20 0.008 230)",
                  }}
                />
                <span
                  style={{
                    fontSize: "9px",
                    fontWeight: 600,
                    color: "oklch(0.55 0.12 145)",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    whiteSpace: "nowrap",
                  }}
                >
                  → {PLAY_LABELS[item.play] ?? item.play}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: "oklch(0.20 0.008 230)",
                  }}
                />
              </div>
            )
          } else {
            const isActive = item.index === currentEventIndex
            return (
              <NarrativeEntry
                key={item.key}
                event={item.event}
                isActive={isActive}
                onPlayerHover={onPlayerHover}
                onPlayerHoverEnd={onPlayerHoverEnd}
                playerLookup={playerLookup}
                onSeek={onSeek ? () => onSeek(item.index) : undefined}
                entryRef={
                  isActive
                    ? (el: HTMLDivElement | null) => {
                        activeEntryRef.current = el
                      }
                    : undefined
                }
              />
            )
          }
        })}
      </div>
    </div>
  )
}
